/** @file RGBA8 colors packed as one uint32 whose memory bytes are R,G,B,A (little-endian value 0xAABBGGRR). */

export const color = {
  /** Packs channels 0..255 into a uint32 (bytes R,G,B,A in memory order). */
  pack(r: number, g: number, b: number, a: number): number {
    return (((a & 255) << 24) | ((b & 255) << 16) | ((g & 255) << 8) | (r & 255)) >>> 0;
  },
  /** Unpacks a uint32 into [r, g, b, a]. */
  unpack(packed: number, out: [number, number, number, number]): [number, number, number, number] {
    out[0] = packed & 255;
    out[1] = (packed >>> 8) & 255;
    out[2] = (packed >>> 16) & 255;
    out[3] = (packed >>> 24) & 255;
    return out;
  },
  /** Parses #rrggbbaa (or #rrggbb with alpha 255) into a packed uint32; throws on other shapes. */
  parseHex(hex: string): number {
    const m = /^#([0-9a-fA-F]{6})([0-9a-fA-F]{2})?$/.exec(hex);
    if (!m) throw new Error(`invalid color ${hex}`);
    const rgb = parseInt(m[1] ?? '0', 16);
    const a = m[2] ? parseInt(m[2], 16) : 255;
    return color.pack((rgb >>> 16) & 255, (rgb >>> 8) & 255, rgb & 255, a);
  },
  /** Formats a packed uint32 as #rrggbbaa. */
  toHex(packed: number): string {
    const parts = color.unpack(packed, [0, 0, 0, 0]);
    return `#${parts.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
  },
  /** Component-wise linear interpolation in straight sRGB, rounded to nearest. */
  lerp(from: number, to: number, t: number): number {
    const f = color.unpack(from, [0, 0, 0, 0]);
    const g = color.unpack(to, [0, 0, 0, 0]);
    const mix = (i: 0 | 1 | 2 | 3): number => Math.round(f[i] + (g[i] - f[i]) * t);
    return color.pack(mix(0), mix(1), mix(2), mix(3));
  },
};
